import { FC } from 'react';
import { SpaceKind, VaultSpaceView } from 'shared';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  EmptyState,
  EmptyStateImageVariant,
  EmptyStateVariant,
} from '../../../../components/empty-state/index';
import { LoadingCrossfade } from '../../../../components/loading-skeleton/index';
import { useLocale, useScreenSize } from '../../../../hooks/index';
import { getRecipientCardMenuItems } from '../../constants/workspace-card-menu.const';
import { useDownloadSpaceArchive } from '../../hooks/use-download-space-archive.hook';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { RecipientRoomView, WorkspaceCardMenuAction } from '../../types/data-room.types';
import {
  recipientRoomStats,
  spaceCardStatus,
  spaceDescription,
  toSelectedSpace,
} from '../../utils/space-list-item.util';
import { LastActive } from '../components/last-active';
import { SpaceCard } from '../components/space-card';
import { SpaceTile } from '../components/space-tile';
import { WorkspaceCard } from '../components/workspace-card';
import { WorkspaceSectionLabel } from '../components/workspace-section-label';
import { WorkspacesHomePlaceholder } from '../components/workspaces-loading-placeholders';

export type RecipientHomeOverviewProps = {
  isPending: boolean;
  rooms: RecipientRoomView[];
  // A Stakeholder's own cut of the Vault; absent for somebody who has no Vault.
  vault?: VaultSpaceView;
};

/**
 * Home for somebody a room was shared with: a card for each room they can open, the same card the
 * people who own the company see on theirs. A Stakeholder has the Vault card above them, with the
 * folders that are theirs; there is nothing else they are allowed to see here.
 *
 * The card's menu is their own two entries rather than the owner's five: share, settings and
 * delete belong to the room's own people, and Download appears only for a grant that carries it.
 */
export const RecipientHomeOverview: FC<RecipientHomeOverviewProps> = ({
  isPending,
  rooms,
  vault,
}) => {
  const reveal = SidebarTreeStore((state) => state.reveal);
  const { messagesLocale } = useLocale();
  const { isMobile } = useScreenSize();
  const { downloadArchive, pendingArchiveIds } = useDownloadSpaceArchive();

  const openRoom = ({ createdAt, id, name, permission, status }: RecipientRoomView) =>
    reveal({
      breadcrumbs: [],
      folderId: null,
      space: {
        createdAt,
        id,
        isLocked: false,
        kind: SpaceKind.DATA_ROOM,
        name,
        permission,
        status,
      },
    });

  const handleMenuAction = (action: WorkspaceCardMenuAction, room: RecipientRoomView) => {
    if (action === WorkspaceCardMenuAction.VIEW) {
      openRoom(room);

      return;
    }

    void downloadArchive({ space: { id: room.id, kind: SpaceKind.DATA_ROOM } });
  };

  const openVault = () =>
    vault && reveal({ breadcrumbs: [], folderId: null, space: toSelectedSpace(vault) });

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {!isMobile && (
        <div className="flex items-center justify-between border-y border-gray-100 p-4">
          <h2 className="text-base font-semibold text-gray-700">
            <AppFormattedMessage id="HOME" />
          </h2>
        </div>
      )}
      <LoadingCrossfade
        className="min-h-0 flex-1 overflow-y-auto"
        isLoading={isPending}
        skeleton={<WorkspacesHomePlaceholder />}
      >
        {() =>
          rooms.length === 0 && !vault ? (
            <EmptyState
              colorScheme={EmptyStateVariant.ORANGE}
              description={messagesLocale['NO_SHARED_DATA_ROOMS_HINT']}
              imageVariant={EmptyStateImageVariant.CLIPBOARD}
              text={messagesLocale['NO_SHARED_DATA_ROOMS_YET']}
            />
          ) : (
            <div className={twMerge('flex flex-col gap-4', !isMobile && 'px-4 py-4')}>
              {vault && (
                <>
                  <WorkspaceSectionLabel title="VAULT" />
                  {isMobile ? (
                    <div className="grid grid-cols-2 gap-3">
                      <SpaceTile
                        description={spaceDescription(vault)}
                        kind={vault.kind}
                        name={vault.name}
                        onOpen={openVault}
                        status={spaceCardStatus(toSelectedSpace(vault))}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-4">
                      <SpaceCard onClick={openVault} space={vault} />
                    </div>
                  )}
                </>
              )}
              {rooms.length > 0 && <WorkspaceSectionLabel title="DATA_ROOMS" />}
              {rooms.length === 0 ? null : isMobile ? (
                <div className="grid grid-cols-2 gap-3">
                  {rooms.map((room) => (
                    <SpaceTile
                      description={room.description ?? ''}
                      key={room.id}
                      kind={SpaceKind.DATA_ROOM}
                      name={room.name}
                      onOpen={() => openRoom(room)}
                      status={spaceCardStatus({ kind: SpaceKind.DATA_ROOM, status: room.status })}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {rooms.map((room) => (
                    <WorkspaceCard
                      description={room.description ?? ''}
                      isDownloading={pendingArchiveIds.has(room.id)}
                      key={room.id}
                      kind={SpaceKind.DATA_ROOM}
                      lastUpdated={
                        <LastActive at={room.metrics.lastActivityAt ?? room.updatedAt} />
                      }
                      menuItems={getRecipientCardMenuItems(room.permission)}
                      onClick={() => openRoom(room)}
                      onMenuAction={(action) => handleMenuAction(action, room)}
                      showMenu
                      stats={recipientRoomStats(room.metrics)}
                      status={spaceCardStatus({ kind: SpaceKind.DATA_ROOM, status: room.status })}
                      title={room.name}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        }
      </LoadingCrossfade>
    </div>
  );
};
